/**
 * ERP-FIN-02 Gate AR-04A/C/D: Customer AR 360° Workspace
 * Route: /ar/customers/:cid/statement  (ar.statement.view)
 *
 * Provides:
 *   - Statement preview (as-of-date, start-date, LE filter)
 *   - Full transaction ledger with running balance
 *   - Opening / closing balance summary
 *   - Unapplied cash and credit visibility
 *   - Issue formal statement (PDF + DCC archival, requires ar.statement.generate)
 *   - List of issued statements with PDF download
 *   - Audit trail
 *
 * Financial invariants enforced server-side — this page is read/trigger only.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Download, RefreshCw, Send, Clock,
  ChevronLeft, AlertCircle, CheckCircle2, TrendingUp,
  TrendingDown, DollarSign, History, Eye,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { arStatementApi, formatError } from "@/services/api";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency, minimumFractionDigits: 0,
  }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

const TX_TYPE_CFG = {
  INVOICE:                { label: "Invoice",     color: "#1D4ED8", bg: "#EFF6FF" },
  RECEIPT_ALLOCATION:     { label: "Receipt",     color: "#166534", bg: "#F0FDF4" },
  CREDIT_NOTE_APPLICATION:{ label: "Credit Note", color: "#0176D3", bg: "#F0F7FF" },
  ADVANCE:                { label: "Advance",     color: "#059669", bg: "#ECFDF5" },
  REFUND:                 { label: "Refund",       color: "#DC2626", bg: "#FEF2F2" },
};

function TxTypePill({ type }) {
  const c = TX_TYPE_CFG[type] || { label: type, color: "#6B7280", bg: "#F3F4F6" };
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 20,
      fontSize: 10, fontWeight: 700, color: c.color, background: c.bg,
    }}>{c.label}</span>
  );
}

function SummaryCard({ label, value, sub, variant = "neutral" }) {
  const colors = {
    neutral: { bg: "#F8FAFC", border: "#E2E8F0", text: "#181818" },
    blue:    { bg: "#EFF6FF", border: "#BFDBFE", text: "#1D4ED8" },
    green:   { bg: "#F0FDF4", border: "#BBF7D0", text: "#166534" },
    red:     { bg: "#FEF2F2", border: "#FECACA", text: "#DC2626" },
    amber:   { bg: "#FFFBEB", border: "#FDE68A", text: "#92400E" },
  };
  const c = colors[variant] || colors.neutral;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12,
      padding: "14px 18px", minWidth: 140,
    }}>
      <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: c.text }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ARCustomer360Workspace() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { user }  = useAuth();

  const hasCap = (cap) => user?.capabilities?.includes(cap);
  const canView     = hasCap("ar.statement.view");
  const canGenerate = hasCap("ar.statement.generate");

  // ── Filter state ────────────────────────────────────────────────────────────
  const [asOfDate,      setAsOfDate]      = useState(today());
  const [startDate,     setStartDate]     = useState("");
  const [leId,          setLeId]          = useState("");

  // ── Statement state ─────────────────────────────────────────────────────────
  const [preview,       setPreview]       = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [issuing,       setIssuing]       = useState(false);
  const [issueNotes,    setIssueNotes]    = useState("");
  const [showIssueForm, setShowIssueForm] = useState(false);

  // ── Issued statements list ───────────────────────────────────────────────────
  const [statements,    setStatements]    = useState([]);
  const [stmtLoading,   setStmtLoading]   = useState(false);

  // ── Active view tab ─────────────────────────────────────────────────────────
  const [activeTab,     setActiveTab]     = useState("preview"); // preview | history

  const loadPreview = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const params = { as_of_date: asOfDate };
      if (startDate) params.start_date = startDate;
      if (leId)      params.legal_entity_id = leId;
      const res = await arStatementApi.getPreview(cid, params);
      setPreview(res.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [cid, asOfDate, startDate, leId]);

  const loadStatements = useCallback(async () => {
    if (!cid) return;
    setStmtLoading(true);
    try {
      const res = await arStatementApi.list({ customer_id: cid, page: 1, page_size: 20 });
      setStatements(res.data?.statements || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setStmtLoading(false);
    }
  }, [cid]);

  useEffect(() => { loadPreview(); }, [loadPreview]);
  useEffect(() => { loadStatements(); }, [loadStatements]);

  const handleIssue = async () => {
    setIssuing(true);
    try {
      const body = { as_of_date: asOfDate, statement_notes: issueNotes || undefined };
      if (startDate) body.start_date = startDate;
      if (leId)      body.legal_entity_id = leId;
      const res = await arStatementApi.issue(cid, body);
      toast.success(`Statement ${res.data.statement_number} issued`);
      setShowIssueForm(false);
      setIssueNotes("");
      loadStatements();
      setActiveTab("history");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setIssuing(false);
    }
  };

  const downloadPdf = (stmtId, stmtNum) => {
    const url = `${API}/api/ar/statements/${stmtId}/pdf`;
    const a   = document.createElement("a");
    a.href    = url;
    a.download = `${stmtNum || "statement"}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!canView) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <AlertCircle size={32} color="#DC2626" />
        <p style={{ marginTop: 12, color: "#6B7280" }}>You do not have permission to view AR statements.</p>
      </div>
    );
  }

  const p = preview;

  return (
    <div data-testid="ar-customer360-workspace" style={{ padding: "24px 28px", fontFamily: "'Inter', sans-serif", background: "#F7F9FC", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <button
          data-testid="back-to-aging-btn"
          onClick={() => navigate("/ar/aging")}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#FFF", color: "#374151", cursor: "pointer", fontSize: 13 }}
        >
          <ChevronLeft size={15} /> AR Aging
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#181818", margin: 0 }}>
            Customer AR 360°
          </h1>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
            Customer ID: {cid}
            {p?.customer_name && ` · ${p.customer_name}`}
            {p?.customer_gstin && ` · GSTIN: ${p.customer_gstin}`}
          </div>
        </div>
        <button
          data-testid="refresh-preview-btn"
          onClick={loadPreview}
          disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#FFF", cursor: "pointer", fontSize: 13 }}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        {canGenerate && (
          <button
            data-testid="issue-statement-btn"
            onClick={() => setShowIssueForm(true)}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 8, border: "none", background: "#0176D3", color: "#FFF", cursor: "pointer", fontWeight: 700, fontSize: 13 }}
          >
            <Send size={14} /> Issue Statement
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ background: "#FFF", borderRadius: 12, border: "1px solid #E2E8F0", padding: "16px 20px", marginBottom: 20, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 4 }}>As-of Date *</label>
          <input
            data-testid="as-of-date-input"
            type="date"
            value={asOfDate}
            onChange={e => setAsOfDate(e.target.value)}
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 4 }}>From Date (optional)</label>
          <input
            data-testid="start-date-input"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 4 }}>Legal Entity ID (optional)</label>
          <input
            data-testid="le-filter-input"
            type="text"
            placeholder="LE ID..."
            value={leId}
            onChange={e => setLeId(e.target.value)}
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, width: 180 }}
          />
        </div>
        <button
          data-testid="load-statement-btn"
          onClick={loadPreview}
          style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: "#0176D3", color: "#FFF", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
        >
          Load Statement
        </button>
      </div>

      {/* Issue Statement Modal */}
      {showIssueForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div data-testid="issue-statement-modal" style={{ background: "#FFF", borderRadius: 16, padding: 28, width: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Issue Formal Statement</h3>
            <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 16 }}>
              This will generate an immutable PDF, archive in DCC, and assign a statement number.
              As-of: <strong>{asOfDate}</strong>
            </p>
            <label style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 4 }}>Notes (optional)</label>
            <textarea
              data-testid="issue-notes-input"
              value={issueNotes}
              onChange={e => setIssueNotes(e.target.value)}
              placeholder="e.g. Monthly reconciliation statement"
              rows={3}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, resize: "vertical" }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button
                data-testid="confirm-issue-btn"
                onClick={handleIssue}
                disabled={issuing}
                style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: "#0176D3", color: "#FFF", fontWeight: 700, cursor: "pointer", fontSize: 13 }}
              >
                {issuing ? "Issuing..." : "Issue Statement"}
              </button>
              <button
                data-testid="cancel-issue-btn"
                onClick={() => setShowIssueForm(false)}
                style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#FFF", cursor: "pointer", fontSize: 13 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        {[
          { key: "preview", label: "Statement Preview", icon: Eye },
          { key: "history", label: "Issued Statements", icon: History },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`tab-${key}`}
            onClick={() => setActiveTab(key)}
            style={{
              padding: "8px 18px", borderRadius: "8px 8px 0 0", fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none",
              background: activeTab === key ? "#FFF" : "transparent",
              color: activeTab === key ? "#0176D3" : "#6B7280",
              borderBottom: activeTab === key ? "2px solid #0176D3" : "2px solid transparent",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* ── PREVIEW TAB ─────────────────────────────────────────────────────── */}
      {activeTab === "preview" && (
        <div>
          {loading && (
            <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}>Loading statement…</div>
          )}

          {!loading && p && (
            <>
              {/* Summary Cards */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
                <SummaryCard label="Total Invoiced"   value={fmt(p.total_invoiced_minor)}   variant="blue" />
                <SummaryCard label="Receipts Applied" value={fmt(p.total_allocated_minor)}  variant="green"
                  sub={`${p.allocation_count} allocation${p.allocation_count !== 1 ? "s" : ""}`}
                />
                <SummaryCard label="Credits Applied"  value={fmt(p.total_cn_applied_minor)} variant="green"
                  sub={`${p.cn_application_count} application${p.cn_application_count !== 1 ? "s" : ""}`}
                />
                {p.total_advances_minor > 0 && (
                  <SummaryCard label="Advances"        value={fmt(p.total_advances_minor)}   variant="amber" />
                )}
                {p.total_refunds_minor > 0 && (
                  <SummaryCard label="Refunds"         value={fmt(p.total_refunds_minor)}    variant="red" />
                )}
                <SummaryCard label="Closing Balance"  value={fmt(p.closing_balance_minor)}  variant={p.closing_balance_minor > 0 ? "red" : "green"}
                  sub={`As of ${p.as_of_date}`}
                />
              </div>

              {/* Unapplied warnings */}
              {(p.unapplied_cash_minor > 0 || p.unapplied_credit_minor > 0) && (
                <div data-testid="unapplied-section" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", gap: 16 }}>
                  <AlertCircle size={16} color="#D97706" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div style={{ fontSize: 13, color: "#92400E" }}>
                    <strong>Unapplied balance available:</strong>
                    {p.unapplied_cash_minor > 0 && <span> Cash {fmt(p.unapplied_cash_minor)}</span>}
                    {p.unapplied_cash_minor > 0 && p.unapplied_credit_minor > 0 && " · "}
                    {p.unapplied_credit_minor > 0 && <span> Credit Notes {fmt(p.unapplied_credit_minor)}</span>}
                  </div>
                </div>
              )}

              {/* Transaction Ledger */}
              <div style={{ background: "#FFF", borderRadius: 12, border: "1px solid #E2E8F0", overflow: "hidden" }}>
                <div style={{ padding: "14px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#181818" }}>Transaction Ledger</span>
                  <span style={{ fontSize: 12, color: "#6B7280" }}>{p.transactions?.length || 0} entries · Period: {p.start_date || "All"} → {p.as_of_date}</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table data-testid="transaction-ledger-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#F8FAFC" }}>
                        {["Date", "Type", "Reference", "Description", "Debit", "Credit", "Balance"].map(h => (
                          <th key={h} style={{ padding: "10px 14px", textAlign: h === "Debit" || h === "Credit" || h === "Balance" ? "right" : "left", color: "#374151", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(!p.transactions || p.transactions.length === 0) ? (
                        <tr>
                          <td colSpan={7} style={{ padding: "24px", textAlign: "center", color: "#9CA3AF" }}>
                            No transactions in this period.
                          </td>
                        </tr>
                      ) : p.transactions.map((tx, i) => (
                        <tr key={i} data-testid={`tx-row-${i}`} style={{ borderBottom: "1px solid #F1F5F9", background: i % 2 === 0 ? "#FFF" : "#FAFAFA" }}>
                          <td style={{ padding: "9px 14px", color: "#374151" }}>{tx.date?.slice(0, 10) || "—"}</td>
                          <td style={{ padding: "9px 14px" }}><TxTypePill type={tx.type} /></td>
                          <td style={{ padding: "9px 14px", color: "#374151", fontFamily: "monospace", fontSize: 11 }}>{tx.reference || "—"}</td>
                          <td style={{ padding: "9px 14px", color: "#6B7280", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.description || "—"}</td>
                          <td style={{ padding: "9px 14px", textAlign: "right", color: tx.debit_minor ? "#DC2626" : "#9CA3AF" }}>
                            {tx.debit_minor ? fmt(tx.debit_minor) : "—"}
                          </td>
                          <td style={{ padding: "9px 14px", textAlign: "right", color: tx.credit_minor ? "#166534" : "#9CA3AF" }}>
                            {tx.credit_minor ? fmt(tx.credit_minor) : "—"}
                          </td>
                          <td style={{ padding: "9px 14px", textAlign: "right", fontWeight: 700, color: (tx.running_balance_minor || 0) > 0 ? "#DC2626" : "#166534" }}>
                            {fmt(Math.abs(tx.running_balance_minor || 0))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {!loading && !p && (
            <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}>
              Select a date range and click Load Statement.
            </div>
          )}
        </div>
      )}

      {/* ── HISTORY TAB ─────────────────────────────────────────────────────── */}
      {activeTab === "history" && (
        <div style={{ background: "#FFF", borderRadius: 12, border: "1px solid #E2E8F0", overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#181818" }}>Issued Statements</span>
            <button onClick={loadStatements} style={{ fontSize: 12, color: "#0176D3", background: "none", border: "none", cursor: "pointer" }}>
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
          {stmtLoading ? (
            <div style={{ padding: 24, textAlign: "center", color: "#6B7280" }}>Loading…</div>
          ) : statements.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#9CA3AF" }}>No statements issued yet.</div>
          ) : (
            <table data-testid="statements-history-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  {["Number", "As-of Date", "Closing Balance", "Invoice Count", "Issued By", "Issued At", "PDF"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: "#374151", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #E2E8F0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {statements.map((s, i) => (
                  <tr key={s.id} data-testid={`stmt-row-${i}`} style={{ borderBottom: "1px solid #F1F5F9" }}>
                    <td style={{ padding: "10px 14px", fontFamily: "monospace", fontWeight: 600, color: "#0176D3" }}>{s.statement_number || "—"}</td>
                    <td style={{ padding: "10px 14px" }}>{fmtDate(s.as_of_date)}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: (s.closing_balance_minor || 0) > 0 ? "#DC2626" : "#166534" }}>{fmt(s.closing_balance_minor)}</td>
                    <td style={{ padding: "10px 14px" }}>{s.invoice_count ?? "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#6B7280" }}>{s.issued_by_email || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#6B7280" }}>{fmtDate(s.issued_at)}</td>
                    <td style={{ padding: "10px 14px" }}>
                      {s.pdf_id ? (
                        <button
                          data-testid={`download-pdf-btn-${s.id}`}
                          onClick={() => downloadPdf(s.id, s.statement_number)}
                          style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: "1px solid #0176D3", background: "#EFF6FF", color: "#0176D3", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                        >
                          <Download size={11} /> PDF
                        </button>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
